import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  Megaphone,
  Plus,
  Trash2,
  Download,
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Globe,
  Lock
} from 'lucide-react';

const PAGE_LIMIT = 10;

const AnnouncementsDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('public');
  const [page, setPage] = useState(1);
  const [expandedDocs, setExpandedDocs] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const { data, isLoading } = useQuery(
    ['announcements', activeTab, page],
    () => api.get(`/announcements?visibility=${activeTab}&page=${page}&limit=${PAGE_LIMIT}`).then(r => r.data),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  // Reset to page 1 when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedDocs(null);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setExpandedDocs(null);
  };

  const deleteMutation = useMutation(
    (id) => api.delete(`/announcements/${id}`),
    {
      onSuccess: () => {
        toast.success('Circular deleted successfully');
        queryClient.invalidateQueries('announcements');
        setDeleteConfirm(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to delete circular');
      }
    }
  );

  const handleDownload = async (announcementId, index, filename) => {
    try {
      const res = await api.get(`/announcements/${announcementId}/documents/download?index=${index}`);
      window.open(res.data.url, '_blank');
    } catch {
      toast.error('Failed to generate download link');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const announcements = data?.announcements || [];
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Circulars &amp; Announcements</h1>
          <p className="text-sm text-secondary-500 mt-1">Official circulars and notices</p>
        </div>
        {['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user?.role) && (
          <button
            onClick={() => navigate('/announcements/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            New Circular
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-secondary-200 mb-6">
        <button
          onClick={() => handleTabChange('public')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'public'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-secondary-500 hover:text-secondary-700'
          }`}
        >
          <Globe className="h-4 w-4" />
          Public Circulars
        </button>
        <button
          onClick={() => handleTabChange('staff')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'staff'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-secondary-500 hover:text-secondary-700'
          }`}
        >
          <Lock className="h-4 w-4" />
          Staff Only
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-secondary-200">
          <Megaphone className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
          <p className="text-secondary-500 font-medium">No circulars found</p>
          <p className="text-secondary-400 text-sm mt-1">
            {activeTab === 'public' ? 'No public circulars have been issued.' : 'No staff circulars have been issued.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((item) => (
            <div key={item.id} className="bg-white rounded-lg border border-secondary-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                      item.visibility === 'public'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {item.visibility === 'public' ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                      {item.visibility === 'public' ? 'Public' : 'Staff Only'}
                    </span>
                    <span className="text-xs text-secondary-400">{formatDate(item.created_at)}</span>
                    {item.author && (
                      <span className="text-xs text-secondary-400">
                        · by {item.author.fullName}
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-semibold text-secondary-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-secondary-600 whitespace-pre-wrap">{item.message}</p>

                  {/* Documents */}
                  {item.documents && item.documents.length > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={() => setExpandedDocs(expandedDocs === item.id ? null : item.id)}
                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        <FileText className="h-4 w-4" />
                        {item.documents.length} Attachment{item.documents.length > 1 ? 's' : ''}
                        {expandedDocs === item.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {expandedDocs === item.id && (
                        <div className="mt-2 space-y-1">
                          {item.documents.map((doc, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleDownload(item.id, idx, doc.originalName)}
                              className="flex items-center gap-2 text-sm text-secondary-700 hover:text-primary-600 py-1 px-2 rounded hover:bg-secondary-50 w-full text-left"
                            >
                              <Download className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate">{doc.originalName}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {(item.createdBy === user?.id || item.created_by === user?.id) && (
                  <div className="flex-shrink-0">
                    {deleteConfirm === item.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-secondary-500">Delete?</span>
                        <button
                          onClick={() => deleteMutation.mutate(item.id)}
                          disabled={deleteMutation.isLoading}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-2 py-1 bg-secondary-200 text-secondary-700 text-xs rounded hover:bg-secondary-300"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(item.id)}
                        className="p-1.5 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded"
                        title="Delete circular"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-5">
          <p className="text-sm text-secondary-500">
            Showing {((pagination.page - 1) * PAGE_LIMIT) + 1}–
            {Math.min(pagination.page * PAGE_LIMIT, pagination.total)} of {pagination.total} circulars
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-secondary-300 rounded-md text-secondary-700 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
            <span className="text-sm text-secondary-600 px-2">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-secondary-300 rounded-md text-secondary-700 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsDashboard;
